import { useCallback } from 'react';
import { z } from 'zod';
import { EmptyValidationMessage, calculateAge, parseIsoDateTime } from '@ninja/utilities';
import { PolicyType, Relationship, getYearOfBirth } from '../../../../../common';
import {
	useDateOfBirthField,
	useMaritalStatusField,
	useNationalityField,
	useRelationshipField,
} from '../../../members/members-form/fields';
import { prepareFieldError } from '../../../members/members-form/helpers/add-field-error';
import { getFieldTypeBasedOnRelation } from '../../../members/members-form/helpers/get-field-type-based-on-relation';
import { useEmiratesIdField, useIsWithEidField } from '../fields';
import { IEidMembersForm } from '../types';

const isDisabled = (relationshipTypeId?: Relationship, dateOfBirth?: string): boolean => {
	if (!relationshipTypeId) {
		return false;
	}

	switch (relationshipTypeId) {
		case Relationship.Spouse:
			return true;
		case Relationship.Daughter:
		case Relationship.Son: {
			const birthDate = dateOfBirth ? parseIsoDateTime(dateOfBirth) : undefined;

			return birthDate ? calculateAge(birthDate) < 18 : false;
		}
	}

	return false;
};

export const useEidDependentsFieldsStructure = () => {
	const dateOfBirthField = useDateOfBirthField<IEidMembersForm>();
	const emiratesIdField = useEmiratesIdField<IEidMembersForm>();
	const nationalityField = useNationalityField<IEidMembersForm>();
	const relationshipField = useRelationshipField<IEidMembersForm>();
	const maritalStatusField = useMaritalStatusField<IEidMembersForm>();
	const isWithEidField = useIsWithEidField<IEidMembersForm>();
	const fieldsStructure = useCallback(
		(index: number, relationshipTypeId?: Relationship, dateOfBirth?: string, isWithEID?: number) => [
			...(isWithEID ? [emiratesIdField.getField({ field: `dependents.${index}.emiratesIdNumber` })] : []),
			relationshipField.getField({ field: `dependents.${index}.relationshipTypeId` }),
			...(!isWithEID ? [nationalityField.getField({ field: `dependents.${index}.nationalityId` })] : []),
			maritalStatusField.getField({
				field: `dependents.${index}.maritalStatusId`,
				disabled: isDisabled(relationshipTypeId, dateOfBirth),
			}),
			dateOfBirthField.getField({ field: `dependents.${index}.dateOfBirth`, fieldType: 'child' }),
		],
		[relationshipField.getField, dateOfBirthField.getField, nationalityField.getField, maritalStatusField.getField]
	);

	const isWithEidFieldsStructure = (index: number) => [
		isWithEidField.getField({ field: `dependents.${index}.isWithEID` }),
	];

	const getMaximumDependentsAllowed = (policyTypeId: number, maximumNoOfMember: number) => {
		if (policyTypeId === PolicyType.MeAndFamily) {
			// Remove 1 for principal
			return maximumNoOfMember - 1;
		}

		return maximumNoOfMember;
	};

	const getValidations = ({
		formValues,
		maximumNoOfMember,
	}: {
		formValues: IEidMembersForm;
		maximumNoOfMember: number;
	}) => {
		return z.object({
			dependents: z
				.object({
					relationshipTypeId: z.number().optional(),
					dateOfBirth: z.string().optional(),
					nationalityId: z.number().optional(),
					memberId: z.string().optional(),
					maritalStatusId: z.number().optional(),
					emiratesIdNumber: z.string().optional(),
					isWithEID: z.number().optional(),
				})
				.superRefine((values, ctx) => {
					const { emiratesIdNumber, dateOfBirth, relationshipTypeId, nationalityId, maritalStatusId, isWithEID } =
						values;
					const addFieldError = prepareFieldError(ctx);

					if (!isWithEID) {
						nationalityField.getValidation(nationalityId, formValues.policyTypeId, relationshipTypeId, ctx);
					}

					if (isWithEID) emiratesIdField.validate(emiratesIdNumber, addFieldError, getYearOfBirth(dateOfBirth), ctx);

					if (!relationshipTypeId) addFieldError({ field: 'relationshipTypeId', message: EmptyValidationMessage });

					if (!dateOfBirth) addFieldError({ field: 'dateOfBirth', message: EmptyValidationMessage });

					if (relationshipTypeId) relationshipField.getValidation({ addFieldError, formValues, relationshipTypeId });

					if (!maritalStatusId) addFieldError({ field: 'maritalStatusId', message: EmptyValidationMessage });

					if (dateOfBirth)
						dateOfBirthField.getValidation({
							formValues,
							fieldType: getFieldTypeBasedOnRelation(relationshipTypeId),
							addFieldError,
							dateOfBirth,
						});
				})
				.array()
				.max(getMaximumDependentsAllowed(formValues.policyTypeId ?? PolicyType.OnlyMe, maximumNoOfMember)),
		});
	};

	return {
		fieldsStructure,
		isWithEidFieldsStructure,
		getValidations,
	};
};
